/**
 * The person's messages from the sandbox admin, shared by the notifications bell and the app-wide banner: one live
 * poll (utils/livePoll.js: every 45 s while the tab is visible, at once when it comes back, slower after errors) for
 * every component using it, so a new message, an edit or a read elsewhere shows without a reload. `messages` is null
 * until the first answer, or where the gateway has none.
 *
 * What this tab did (read, dismissed) stays applied over the answers until the gateway says so too, so a poll that
 * left before the click never brings back a closed banner or an unread count.
 */
import { dismissMessage, getMessages, markMessagesRead } from '../services/SandboxMessagesService';
import { createLivePoll, useLivePoll } from '../utils/livePoll';

// id -> the version read / dismissed here, until the gateway's answer agrees (or the message changes).
const readHere = new Map();
const dismissedHere = new Map();

const unreadOf = (items) => items.filter((i) => !i.read).length;

/** The gateway's answer with this tab's own reads and dismissals on top. */
function withLocal(answer) {
  if (!answer) { return null; }
  let flipped = 0;
  const items = (answer.items || []).map((item) => {
    let m = item;
    if (readHere.has(item.id)) {
      if (item.read || readHere.get(item.id) !== item.version) { readHere.delete(item.id); } else { m = { ...m, read: true }; flipped += 1; }
    }
    if (dismissedHere.has(item.id)) {
      if (item.dismissed || dismissedHere.get(item.id) !== item.version) { dismissedHere.delete(item.id); } else { m = { ...m, dismissed: true }; }
    }
    return m;
  });
  return { items, unread: Math.max(0, (answer.unread ?? unreadOf(answer.items || [])) - flipped) };
}

const poll = createLivePoll({
  load: async() => withLocal(await getMessages()),
  onClear: () => { readHere.clear(); dismissedHere.clear(); },
});

export function refreshMessages() {
  return poll.refresh();
}

export function readMessages(ids) {
  const state = poll.getSnapshot();
  if (!state) { return Promise.resolve(); }
  const all = !ids;
  const items = state.items.map((i) => {
    if (!(all || ids.includes(i.id)) || i.read) { return i; }
    readHere.set(i.id, i.version);
    return { ...i, read: true };
  });
  poll.set({ items, unread: Math.max(0, state.unread - (unreadOf(state.items) - unreadOf(items))) });
  return markMessagesRead(all ? { all: true } : { ids }).catch(() => {});
}

export function dismissBanner(id) {
  const state = poll.getSnapshot();
  if (!state) { return Promise.resolve(); }
  poll.set({
    ...state,
    items: state.items.map((i) => {
      if (i.id !== id) { return i; }
      dismissedHere.set(i.id, i.version);
      return { ...i, dismissed: true };
    }),
  });
  return dismissMessage(id).catch(() => {});
}

/** For tests: forget everything. */
export function resetSandboxMessages() {
  poll.reset();
}

export default function useSandboxMessages() {
  const messages = useLivePoll(poll);
  return { messages, refresh: refreshMessages, read: readMessages, dismiss: dismissBanner };
}
